import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Package, LogOut, User, ChevronRight, Truck, Clock, CheckCircle2, XCircle, Gift, Save, Share2, Download } from "lucide-react";
import { toast } from "sonner";
import { MolecularBg } from "../components/MolecularBg";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { StoreCreditCard } from "../components/StoreCreditCard";
import { VipMembershipCard } from "../components/VipMembershipCard";
import { VipProgressBar } from "../components/VipProgressBar";
import { VipTimeline } from "../components/VipTimeline";
import { VipStatsGrid } from "../components/VipStatsGrid";
import { VipLevelUpModal } from "../components/VipLevelUpModal";
import { VipCardShareModal } from "../components/VipCardShareModal";

const STATUS_META = {
    order_placed: { label: "Order Placed", color: "text-amber-300", bg: "bg-amber-500/10 border-amber-500/30", icon: Clock },
    processing: { label: "Processing", color: "text-blue-300", bg: "bg-blue-500/10 border-blue-500/30", icon: Package },
    shipped: { label: "Shipped", color: "text-[var(--gold)]", bg: "bg-[rgba(233,188,11,0.1)] border-[rgba(233,188,11,0.35)]", icon: Truck },
    out_for_delivery: { label: "Out for Delivery", color: "text-orange-300", bg: "bg-orange-500/10 border-orange-500/30", icon: Truck },
    delivered: { label: "Delivered", color: "text-emerald-300", bg: "bg-emerald-500/10 border-emerald-500/30", icon: CheckCircle2 },
    canceled: { label: "Canceled", color: "text-red-300", bg: "bg-red-500/10 border-red-500/30", icon: XCircle },
};

const PAYMENT_META = {
    paid: { label: "Paid", color: "text-emerald-300", bg: "bg-emerald-500/10 border-emerald-500/30" },
    awaiting_payment: { label: "Unpaid", color: "text-amber-300", bg: "bg-amber-500/10 border-amber-500/30" },
    initiated: { label: "Pending", color: "text-amber-300", bg: "bg-amber-500/10 border-amber-500/30" },
    pending: { label: "Pending", color: "text-amber-300", bg: "bg-amber-500/10 border-amber-500/30" },
    canceled: { label: "Canceled", color: "text-red-300", bg: "bg-red-500/10 border-red-500/30" },
    failed: { label: "Failed", color: "text-red-300", bg: "bg-red-500/10 border-red-500/30" },
};

export const StatusBadge = ({ status, type = "fulfillment" }) => {
    const map = type === "payment" ? PAYMENT_META : STATUS_META;
    const meta = map[status];
    if (!meta) return null;
    return (
        <span className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full border ${meta.bg} ${meta.color}`}>
            {meta.label}
        </span>
    );
};

export const AccountPage = () => {
    const { user, loading, logout, refresh } = useAuth();
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [ordersLoading, setOrdersLoading] = useState(true);
    const [account, setAccount] = useState(null);
    const [celebration, setCelebration] = useState(null);
    const [shareOpen, setShareOpen] = useState(false);

    useEffect(() => {
        if (!loading && !user) navigate("/login", { replace: true, state: { from: "/account" } });
    }, [loading, user, navigate]);

    useEffect(() => {
        if (!user) return;
        (async () => {
            try {
                const [ordersRes, accountRes] = await Promise.all([
                    api.get("/me/orders"),
                    api.get("/me/account"),
                ]);
                setOrders(ordersRes.data);
                setAccount(accountRes.data);
                if (accountRes.data?.pending_celebration) {
                    setCelebration(accountRes.data.pending_celebration);
                }
                if (refresh) refresh();
            } catch {
                toast.error("Could not load your account");
            }
            setOrdersLoading(false);
        })();
    }, [user, refresh]);

    const handleCelebrationSeen = async (tier) => {
        setCelebration(null);
        try {
            await api.post("/me/vip/celebration-seen", { tier });
        } catch {
            /* silent — local state already cleared */
        }
    };

    const handleLogout = async () => {
        await logout();
        toast.success("Signed out");
        navigate("/");
    };

    if (loading || !user) {
        return (
            <div className="pt-28 pb-20 min-h-screen flex items-center justify-center">
                <div className="inline-block w-8 h-8 border-2 border-[var(--gold)] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="pt-28 pb-20 min-h-screen relative" data-testid="account-page">
            <MolecularBg intensity="low" />
            <div className="container-x relative z-10 max-w-6xl">
                <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
                    <div>
                        <span className="eyebrow">// My Account</span>
                        <h1 className="font-display text-4xl md:text-5xl font-extrabold text-white tracking-tighter mt-2 italic">
                            Welcome, <span className="text-[var(--gold)]">{user.name || user.email.split("@")[0]}</span>
                        </h1>
                        <p className="text-sm text-[var(--text-secondary)] mt-2 flex items-center gap-2">
                            <User size={14} /> {user.email}
                        </p>
                    </div>
                    <button
                        onClick={handleLogout}
                        className="px-4 py-2 rounded-full border border-white/10 text-xs font-mono uppercase tracking-widest text-[var(--text-secondary)] hover:text-white hover:border-white/30 inline-flex items-center gap-2"
                        data-testid="logout-btn"
                    >
                        <LogOut size={14} /> Sign out
                    </button>
                </div>

                {account && (
                    <>
                        {/* Hero: Premium VIP Membership Card + Store Credit side-by-side */}
                        <div className="grid lg:grid-cols-3 gap-4 mb-5" data-testid="account-summary">
                            <div className="lg:col-span-2">
                                <VipMembershipCard
                                    tier={account.user.vip_tier}
                                    customerName={user.name || user.email.split("@")[0]}
                                    lifetimeSpend={account.user.lifetime_spend}
                                    discountPct={account.user.vip_discount_pct}
                                    naturalTier={account.natural_tier}
                                    overridden={!!account.manual_override}
                                />
                            </div>
                            <div className="flex flex-col gap-3">
                                <StoreCreditCard balance={account.user.store_credit_balance} />
                                <button
                                    onClick={() => setShareOpen(true)}
                                    className="vip-stat-card glass-elevated rounded-2xl p-4 text-left hover:border-[var(--gold)]/40 transition-all inline-flex items-center gap-3"
                                    data-testid="open-share-vip-card"
                                >
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--gold)]/30 to-transparent border border-[var(--gold)]/40 flex items-center justify-center shrink-0">
                                        <Share2 size={16} className="text-[var(--gold)]" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[var(--text-muted)]">
                                            Show off your tier
                                        </p>
                                        <p className="text-sm font-display font-bold text-white">
                                            Download / Share VIP Card
                                        </p>
                                    </div>
                                    <Download size={14} className="text-[var(--text-muted)]" />
                                </button>
                            </div>
                        </div>

                        {/* Progress to next tier */}
                        <div className="mb-5">
                            <VipProgressBar progress={account.tier_progress} />
                        </div>

                        {/* Tier timeline */}
                        <div className="mb-5">
                            <VipTimeline currentTier={account.user.vip_tier} tiers={account.tiers} />
                        </div>

                        {/* Stat cards */}
                        <div className="mb-6">
                            <VipStatsGrid
                                stats={{
                                    lifetime_spend: account.user.lifetime_spend,
                                    total_orders: account.stats?.total_orders ?? 0,
                                    discount_pct: account.user.vip_discount_pct,
                                    total_savings: account.stats?.total_savings ?? 0,
                                    wheel_entries: account.stats?.wheel_entries ?? 0,
                                    vip_tier: account.user.vip_tier,
                                }}
                                tier={account.user.vip_tier}
                            />
                        </div>
                    </>
                )}

                {account && <BirthdayCard birthday={account.user.birthday} onSaved={() => {
                    // refresh after save
                    api.get("/me/account").then((r) => setAccount(r.data)).catch(() => {});
                }} />}

                <div className="mb-4">
                    <h2 className="font-display text-xl font-bold text-white mb-1">Order history</h2>
                    <p className="text-xs text-[var(--text-muted)] font-mono uppercase tracking-widest">
                        {orders.length} order{orders.length === 1 ? "" : "s"} on file
                    </p>
                </div>

                {ordersLoading ? (
                    <div className="text-[var(--text-secondary)] py-12 text-center">Loading orders…</div>
                ) : orders.length === 0 ? (
                    <div className="glass rounded-2xl p-10 text-center" data-testid="empty-orders">
                        <Package size={40} className="mx-auto text-[var(--text-muted)] mb-4" />
                        <p className="text-[var(--text-secondary)] mb-5">No orders yet. Time to stock the lab.</p>
                        <Link to="/shop" className="btn-solid inline-block">Browse the catalog</Link>
                    </div>
                ) : (
                    <div className="space-y-3" data-testid="account-orders-list">
                        {orders.map((o, idx) => (
                            <Link
                                key={o.reference || `order-${idx}`}
                                to={`/track/${o.reference}`}
                                className="block glass-elevated rounded-2xl p-5 hover:border-[var(--gold)]/40 transition-colors"
                                data-testid={`order-card-${o.reference}`}
                            >
                                <div className="flex items-start justify-between gap-4 flex-wrap">
                                    <div className="min-w-[200px]">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-mono text-[var(--gold)] font-bold tracking-wider text-sm">
                                                {o.reference}
                                            </span>
                                            <StatusBadge status={o.fulfillment_status || "order_placed"} />
                                            <StatusBadge status={o.payment_status} type="payment" />
                                        </div>
                                        <div className="text-base text-white font-display font-bold mt-1.5">
                                            A${Number(o.amount || 0).toFixed(2)} AUD
                                        </div>
                                        <div className="text-[11px] text-[var(--text-muted)] mt-0.5 font-mono">
                                            {new Date(o.created_at).toLocaleString("en-AU")}
                                        </div>
                                        <div className="text-xs text-[var(--text-secondary)] mt-2 line-clamp-1">
                                            {(o.metadata && o.metadata.items_summary) || (o.items && `${o.items.length} item${o.items.length === 1 ? "" : "s"}`)}
                                        </div>
                                    </div>
                                    <ChevronRight size={18} className="text-[var(--text-muted)] mt-1" />
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>

            {/* Level-up celebration popup (fires once per tier) */}
            {celebration && (
                <VipLevelUpModal
                    tier={celebration}
                    discountPct={account?.tiers?.find((t) => t.slug === celebration)?.discount_pct || 0}
                    onSeen={handleCelebrationSeen}
                />
            )}

            {/* Download / Share VIP card modal */}
            <VipCardShareModal
                open={shareOpen}
                onClose={() => setShareOpen(false)}
                account={account}
            />
        </div>
    );
};


const BirthdayCard = ({ birthday, onSaved }) => {
    const [value, setValue] = useState(birthday || "");
    const [busy, setBusy] = useState(false);

    useEffect(() => { setValue(birthday || ""); }, [birthday]);

    const save = async () => {
        setBusy(true);
        try {
            await api.put("/me/profile", { birthday: value || "" });
            toast.success(value ? "Birthday saved" : "Birthday cleared");
            onSaved?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not save");
        }
        setBusy(false);
    };

    return (
        <div className="glass rounded-2xl p-4 mb-8 flex items-center gap-3 flex-wrap" data-testid="birthday-card">
            <Gift size={18} className="text-[var(--gold)] shrink-0" />
            <div className="flex-1 min-w-[180px]">
                <p className="text-sm text-white font-bold">Get a birthday reward.</p>
                <p className="text-[10px] text-[var(--text-muted)]">Set your birthday and we'll drop A$15 in your wallet every year, automatically.</p>
            </div>
            <input
                type="date"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[var(--gold)]"
                data-testid="birthday-input"
            />
            <button onClick={save} disabled={busy} className="px-3 py-2 rounded-full text-xs font-mono uppercase tracking-widest border border-[var(--gold)]/40 text-[var(--gold)] hover:bg-[rgba(233,188,11,0.08)] disabled:opacity-40 inline-flex items-center gap-1" data-testid="birthday-save">
                <Save size={12} /> Save
            </button>
        </div>
    );
};
